import React, { useState, useEffect, useCallback } from 'react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaUsers, FaKey,
  FaUserCheck, FaUserTimes, FaSave, FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import { getApiUrl } from '../../utils/api';
import './Usuarios.css';

// Columnas de permisos con labels legibles
const PERMISOS_CONFIG = [
  { key: 'cuentas', label: 'Cuentas / Ventas', group: 'Operaciones' },
  { key: 'corteCaja', label: 'Corte de Caja', group: 'Operaciones' },
  { key: 'corteParcial', label: 'Corte Parcial', group: 'Operaciones' },
  { key: 'gastos', label: 'Gastos', group: 'Operaciones' },
  { key: 'liquidacionPedidos', label: 'Liquidar Pedidos', group: 'Operaciones' },
  { key: 'cancelarPedidosProductos', label: 'Cancelar Pedidos/Productos', group: 'Operaciones' },
  { key: 'moverProductos', label: 'Mover Productos', group: 'Operaciones' },
  { key: 'retiros', label: 'Retiros', group: 'Operaciones' },
  { key: 'cortesias', label: 'Cortesias', group: 'Operaciones' },
  { key: 'malasOrdenesGarantias', label: 'Malas Ordenes y Garantias', group: 'Operaciones' },

  { key: 'catalogoPlatillos', label: 'Platillos', group: 'Catalogos' },
  { key: 'catalogoMesas', label: 'Mesas', group: 'Catalogos' },
  { key: 'catalogoMeseros', label: 'Meseros', group: 'Catalogos' },
  { key: 'catalogoClientes', label: 'Clientes', group: 'Catalogos' },
  { key: 'recetas', label: 'Recetas', group: 'Catalogos' },

  { key: 'inventario', label: 'Inventario', group: 'Inventario' },
  { key: 'compra', label: 'Compras', group: 'Inventario' },
  { key: 'existenciasReales', label: 'Existencias Reales', group: 'Inventario' },
  { key: 'Gcriticos', label: 'Criticos', group: 'Inventario' },

  { key: 'reportes', label: 'Reportes Generales', group: 'Reportes' },
  { key: 'productosVendidos', label: 'Productos Vendidos', group: 'Reportes' },
  { key: 'reporteExcel', label: 'Reporte Excel', group: 'Reportes' },
  { key: 'utilidadCostoReporte', label: 'Utilidad y Costo', group: 'Reportes' },

  { key: 'promocion', label: 'Promociones', group: 'Configuracion' },
  { key: 'configuracion', label: 'Configuracion', group: 'Configuracion' },
  { key: 'respaldoMenu', label: 'Respaldo Menu', group: 'Configuracion' },
  { key: 'catalogoUsuarios', label: 'Usuarios', group: 'Configuracion' },
  { key: 'catalogoPerfiles', label: 'Perfiles', group: 'Configuracion' },
];

const Usuarios = () => {
  const toast = useToast();
  const [tabActual, setTabActual] = useState('usuarios');

  // --- Estado Usuarios ---
  const [usuarios, setUsuarios] = useState([]);
  const [perfiles, setPerfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [guardandoUser, setGuardandoUser] = useState(false);
  const [userSeleccionado, setUserSeleccionado] = useState(null);
  const [userAEliminar, setUserAEliminar] = useState(null);
  const [formUser, setFormUser] = useState({
    nombre: '', usuario: '', contrasena: '', idPerfil: '', activo: 1
  });

  // --- Estado Perfiles ---
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [showDeletePerfilModal, setShowDeletePerfilModal] = useState(false);
  const [guardandoPerfil, setGuardandoPerfil] = useState(false);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
  const [perfilAEliminar, setPerfilAEliminar] = useState(null);
  const [formPerfil, setFormPerfil] = useState({ nombre: '' });
  const [formPermisos, setFormPermisos] = useState({});

  // ═══════════════════════════════════════════════════════════════
  // API CALLS
  // ═══════════════════════════════════════════════════════════════
  const apiCall = useCallback(async (tipo, extra = {}) => {
    const res = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, ...extra })
    });
    return res.json();
  }, []);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [resU, resP] = await Promise.all([
        apiCall('getUsuarios'),
        apiCall('getPerfiles')
      ]);
      if (resU.success) setUsuarios(resU.data || []);
      if (resP.success) setPerfiles(resP.data || []);
    } catch (err) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // ═══════════════════════════════════════════════════════════════
  // USUARIOS CRUD
  // ═══════════════════════════════════════════════════════════════
  const abrirNuevoUsuario = () => {
    setUserSeleccionado(null);
    setFormUser({ nombre: '', usuario: '', contrasena: '', idPerfil: '', activo: 1 });
    setShowUserModal(true);
  };

  const abrirEditarUsuario = (u) => {
    setUserSeleccionado(u);
    setFormUser({
      nombre: u.nombre || '',
      usuario: u.usuario || '',
      contrasena: '',
      idPerfil: u.idPerfil || '',
      activo: parseInt(u.activo) || 0
    });
    setShowUserModal(true);
  };

  const guardarUsuario = async () => {
    if (!formUser.nombre || !formUser.usuario) {
      toast.error('Nombre y usuario son obligatorios');
      return;
    }
    if (!userSeleccionado && !formUser.contrasena) {
      toast.error('La contrasena es obligatoria para nuevos usuarios');
      return;
    }
    if (!formUser.idPerfil) {
      toast.error('Debe seleccionar un perfil');
      return;
    }

    setGuardandoUser(true);
    try {
      const tipo = userSeleccionado ? 'actualizarUsuario' : 'crearUsuario';
      const payload = { ...formUser };
      if (userSeleccionado) payload.idUsuario = userSeleccionado.idUsuario;
      if (!payload.contrasena) delete payload.contrasena;

      const res = await apiCall(tipo, payload);
      if (res.success) {
        toast.success(res.message);
        setShowUserModal(false);
        cargarDatos();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al guardar usuario');
    } finally {
      setGuardandoUser(false);
    }
  };

  const confirmarEliminarUsuario = (u) => {
    setUserAEliminar(u);
    setShowDeleteUserModal(true);
  };

  const eliminarUsuario = async () => {
    try {
      const res = await apiCall('eliminarUsuario', { idUsuario: userAEliminar.idUsuario });
      if (res.success) {
        toast.success(res.message);
        setShowDeleteUserModal(false);
        cargarDatos();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al eliminar usuario');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PERFILES CRUD
  // ═══════════════════════════════════════════════════════════════
  const initPermisos = (perfil = null) => {
    const p = {};
    PERMISOS_CONFIG.forEach(({ key }) => {
      p[key] = perfil ? (String(perfil[key]) === '1' ? '1' : '0') : '0';
    });
    return p;
  };

  const abrirNuevoPerfil = () => {
    setPerfilSeleccionado(null);
    setFormPerfil({ nombre: '' });
    setFormPermisos(initPermisos());
    setShowPerfilModal(true);
  };

  const abrirEditarPerfil = (p) => {
    setPerfilSeleccionado(p);
    setFormPerfil({ nombre: p.nombre || '' });
    setFormPermisos(initPermisos(p));
    setShowPerfilModal(true);
  };

  const togglePermiso = (key) => {
    setFormPermisos(prev => ({
      ...prev,
      [key]: prev[key] === '1' ? '0' : '1'
    }));
  };

  const toggleGrupo = (grupo) => {
    const keys = PERMISOS_CONFIG.filter(p => p.group === grupo).map(p => p.key);
    const allActive = keys.every(k => formPermisos[k] === '1');
    const newVal = allActive ? '0' : '1';
    setFormPermisos(prev => {
      const copy = { ...prev };
      keys.forEach(k => { copy[k] = newVal; });
      return copy;
    });
  };

  const toggleTodos = () => {
    const allActive = PERMISOS_CONFIG.every(({ key }) => formPermisos[key] === '1');
    const newVal = allActive ? '0' : '1';
    setFormPermisos(prev => {
      const copy = { ...prev };
      PERMISOS_CONFIG.forEach(({ key }) => { copy[key] = newVal; });
      return copy;
    });
  };

  const guardarPerfil = async () => {
    if (!formPerfil.nombre) {
      toast.error('El nombre del perfil es obligatorio');
      return;
    }

    setGuardandoPerfil(true);
    try {
      const tipo = perfilSeleccionado ? 'actualizarPerfil' : 'crearPerfil';
      const payload = { nombre: formPerfil.nombre, permisos: formPermisos };
      if (perfilSeleccionado) payload.idPerfil = perfilSeleccionado.idPerfil;

      const res = await apiCall(tipo, payload);
      if (res.success) {
        toast.success(res.message);
        setShowPerfilModal(false);
        cargarDatos();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al guardar perfil');
    } finally {
      setGuardandoPerfil(false);
    }
  };

  const confirmarEliminarPerfil = (p) => {
    setPerfilAEliminar(p);
    setShowDeletePerfilModal(true);
  };

  const eliminarPerfil = async () => {
    try {
      const res = await apiCall('eliminarPerfil', { idPerfil: perfilAEliminar.idPerfil });
      if (res.success) {
        toast.success(res.message);
        setShowDeletePerfilModal(false);
        cargarDatos();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al eliminar perfil');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // FILTRO
  // ═══════════════════════════════════════════════════════════════
  const usuariosFiltrados = usuarios.filter(u => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (u.nombre || '').toLowerCase().includes(q) ||
      (u.usuario || '').toLowerCase().includes(q) ||
      (u.nombrePerfil || '').toLowerCase().includes(q);
  });

  // ═══════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════
  const contarPermisos = (perfil) => {
    let total = 0;
    PERMISOS_CONFIG.forEach(({ key }) => {
      if (String(perfil[key]) === '1') total++;
    });
    return total;
  };

  const contarUsuariosPerfil = (idPerfil) => {
    return usuarios.filter(u => String(u.idPerfil) === String(idPerfil)).length;
  };

  // Agrupar permisos por grupo
  const permisosPorGrupo = {};
  PERMISOS_CONFIG.forEach(p => {
    if (!permisosPorGrupo[p.group]) permisosPorGrupo[p.group] = [];
    permisosPorGrupo[p.group].push(p);
  });

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="usu-container">
      <Header titulo="Usuarios y Perfiles" color="#795548" />

      <div className="usu-content">
        {/* Tabs */}
        <div className="usu-tabs">
          <button
            className={`usu-tab ${tabActual === 'usuarios' ? 'usu-tab--active' : ''}`}
            onClick={() => setTabActual('usuarios')}
          >
            <FaUsers /> Usuarios
          </button>
          <button
            className={`usu-tab ${tabActual === 'perfiles' ? 'usu-tab--active' : ''}`}
            onClick={() => setTabActual('perfiles')}
          >
            <FaKey /> Perfiles
          </button>
        </div>

        {/* ───── TAB USUARIOS ───── */}
        {tabActual === 'usuarios' && (
          <div className="usu-panel">
            <div className="usu-toolbar">
              <div className="usu-search">
                <FaSearch className="usu-search-icon" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, usuario o perfil..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="usu-search-input"
                />
              </div>
              <button className="usu-btn usu-btn--primary" onClick={abrirNuevoUsuario}>
                <FaPlus /> Nuevo Usuario
              </button>
            </div>

            {loading ? (
              <div className="usu-loading">Cargando usuarios...</div>
            ) : (
              <div className="usu-table-wrap">
                <table className="usu-tabla">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Usuario</th>
                      <th>Perfil</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuariosFiltrados.length === 0 ? (
                      <tr><td colSpan="6" className="usu-empty">No se encontraron usuarios</td></tr>
                    ) : (
                      usuariosFiltrados.map(u => (
                        <tr key={u.idUsuario}>
                          <td>{u.idUsuario}</td>
                          <td className="usu-nombre">{u.nombre}</td>
                          <td><code>{u.usuario}</code></td>
                          <td>
                            <span className="usu-badge-perfil">{u.nombrePerfil || 'Sin perfil'}</span>
                          </td>
                          <td>
                            {parseInt(u.activo) === 1 ? (
                              <span className="usu-estado usu-estado--activo"><FaUserCheck /> Activo</span>
                            ) : (
                              <span className="usu-estado usu-estado--inactivo"><FaUserTimes /> Inactivo</span>
                            )}
                          </td>
                          <td className="usu-acciones">
                            <button className="usu-btn-icon usu-btn-icon--edit" title="Editar" onClick={() => abrirEditarUsuario(u)}>
                              <FaEdit />
                            </button>
                            <button className="usu-btn-icon usu-btn-icon--delete" title="Eliminar" onClick={() => confirmarEliminarUsuario(u)}>
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ───── TAB PERFILES ───── */}
        {tabActual === 'perfiles' && (
          <div className="usu-panel">
            <div className="usu-toolbar">
              <div className="usu-toolbar-info">
                <span>{perfiles.length} perfil(es) registrado(s)</span>
              </div>
              <button className="usu-btn usu-btn--primary" onClick={abrirNuevoPerfil}>
                <FaPlus /> Nuevo Perfil
              </button>
            </div>

            {loading ? (
              <div className="usu-loading">Cargando perfiles...</div>
            ) : (
              <div className="usu-perfiles-grid">
                {perfiles.map(p => (
                  <div key={p.idPerfil} className="usu-perfil-card">
                    <div className="usu-perfil-header">
                      <h3><FaKey /> {p.nombre}</h3>
                      <div className="usu-perfil-actions">
                        <button className="usu-btn-icon usu-btn-icon--edit" title="Editar" onClick={() => abrirEditarPerfil(p)}>
                          <FaEdit />
                        </button>
                        <button className="usu-btn-icon usu-btn-icon--delete" title="Eliminar" onClick={() => confirmarEliminarPerfil(p)}>
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <div className="usu-perfil-stats">
                      <span className="usu-perfil-stat">
                        <FaUsers /> {contarUsuariosPerfil(p.idPerfil)} usuario(s)
                      </span>
                      <span className="usu-perfil-stat">
                        <FaKey /> {contarPermisos(p)}/{PERMISOS_CONFIG.length} permisos
                      </span>
                    </div>
                    <div className="usu-perfil-permisos-preview">
                      {PERMISOS_CONFIG.filter(({ key }) => String(p[key]) === '1').slice(0, 8).map(({ key, label }) => (
                        <span key={key} className="usu-perm-tag">{label}</span>
                      ))}
                      {PERMISOS_CONFIG.filter(({ key }) => String(p[key]) === '1').length > 8 && (
                        <span className="usu-perm-tag usu-perm-tag--more">
                          +{PERMISOS_CONFIG.filter(({ key }) => String(p[key]) === '1').length - 8} mas
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {perfiles.length === 0 && (
                  <div className="usu-empty">No hay perfiles registrados</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />

      {/* ═══ MODAL USUARIO ═══ */}
      <FormModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={userSeleccionado ? 'Editar Usuario' : 'Nuevo Usuario'}
        onSave={guardarUsuario}
        isSaving={guardandoUser}
        saveLabel={userSeleccionado ? 'Actualizar' : 'Crear Usuario'}
        size="medium"
      >
        <FormGrid columns={2}>
          <FormField
            label="Nombre completo"
            name="nombre"
            value={formUser.nombre}
            onChange={(e) => setFormUser(prev => ({ ...prev, nombre: e.target.value }))}
            required
            placeholder="Nombre del usuario"
          />
          <FormField
            label="Usuario (login)"
            name="usuario"
            value={formUser.usuario}
            onChange={(e) => setFormUser(prev => ({ ...prev, usuario: e.target.value }))}
            required
            placeholder="Nombre de acceso"
          />
          <FormField
            label={userSeleccionado ? 'Nueva contrasena (dejar vacio para no cambiar)' : 'Contrasena'}
            name="contrasena"
            type="password"
            value={formUser.contrasena}
            onChange={(e) => setFormUser(prev => ({ ...prev, contrasena: e.target.value }))}
            required={!userSeleccionado}
            placeholder={userSeleccionado ? 'Dejar vacio para no cambiar' : 'Contrasena'}
          />
          <FormField
            label="Perfil"
            name="idPerfil"
            type="select"
            value={formUser.idPerfil}
            onChange={(e) => setFormUser(prev => ({ ...prev, idPerfil: e.target.value }))}
            required
            options={perfiles.map(p => ({ value: p.idPerfil, label: p.nombre }))}
            placeholder="Seleccionar perfil..."
          />
        </FormGrid>
        <div className="usu-activo-toggle">
          <label className="usu-toggle-label">
            <span>Estado:</span>
            <button
              type="button"
              className={`usu-toggle-btn ${formUser.activo === 1 ? 'usu-toggle-btn--on' : 'usu-toggle-btn--off'}`}
              onClick={() => setFormUser(prev => ({ ...prev, activo: prev.activo === 1 ? 0 : 1 }))}
            >
              {formUser.activo === 1 ? <><FaToggleOn /> Activo</> : <><FaToggleOff /> Inactivo</>}
            </button>
          </label>
        </div>
      </FormModal>

      {/* ═══ MODAL PERFIL ═══ */}
      <FormModal
        isOpen={showPerfilModal}
        onClose={() => setShowPerfilModal(false)}
        title={perfilSeleccionado ? 'Editar Perfil' : 'Nuevo Perfil'}
        onSave={guardarPerfil}
        isSaving={guardandoPerfil}
        saveLabel={perfilSeleccionado ? 'Actualizar Perfil' : 'Crear Perfil'}
        size="large"
      >
        <div className="usu-perfil-form">
          <FormField
            label="Nombre del perfil"
            name="nombre"
            value={formPerfil.nombre}
            onChange={(e) => setFormPerfil({ nombre: e.target.value })}
            required
            placeholder="Ej: Administrador, Cajero, Mesero..."
          />

          <div className="usu-permisos-section">
            <div className="usu-permisos-header">
              <h4>Permisos</h4>
              <button type="button" className="usu-btn usu-btn--small" onClick={toggleTodos}>
                {PERMISOS_CONFIG.every(({ key }) => formPermisos[key] === '1') ? 'Desmarcar Todos' : 'Marcar Todos'}
              </button>
            </div>

            {Object.keys(permisosPorGrupo).map(grupo => {
              const keys = permisosPorGrupo[grupo].map(p => p.key);
              const allActive = keys.every(k => formPermisos[k] === '1');

              return (
                <div key={grupo} className="usu-perm-grupo">
                  <div className="usu-perm-grupo-header">
                    <h5>{grupo}</h5>
                    <button
                      type="button"
                      className={`usu-btn-toggle-grupo ${allActive ? 'active' : ''}`}
                      onClick={() => toggleGrupo(grupo)}
                    >
                      {allActive ? 'Desmarcar' : 'Marcar Todo'}
                    </button>
                  </div>
                  <div className="usu-perm-grupo-items">
                    {permisosPorGrupo[grupo].map(({ key, label }) => (
                      <label key={key} className={`usu-perm-item ${formPermisos[key] === '1' ? 'usu-perm-item--on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={formPermisos[key] === '1'}
                          onChange={() => togglePermiso(key)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </FormModal>

      {/* ═══ CONFIRM DELETE USUARIO ═══ */}
      <ConfirmModal
        isOpen={showDeleteUserModal}
        onClose={() => setShowDeleteUserModal(false)}
        onConfirm={eliminarUsuario}
        title="Eliminar Usuario"
        message={`Estas seguro de eliminar al usuario "${userAEliminar?.nombre}" (${userAEliminar?.usuario})? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
      />

      {/* ═══ CONFIRM DELETE PERFIL ═══ */}
      <ConfirmModal
        isOpen={showDeletePerfilModal}
        onClose={() => setShowDeletePerfilModal(false)}
        onConfirm={eliminarPerfil}
        title="Eliminar Perfil"
        message={`Estas seguro de eliminar el perfil "${perfilAEliminar?.nombre}"? Los usuarios asignados a este perfil perderan sus permisos.`}
        confirmLabel="Eliminar"
        type="danger"
      />
    </div>
  );
};

export default Usuarios;
